/**
 * Event v2 micro-label type scale (WEB-736).
 *
 * The design source (`Event v2 - 1c Resolved.dc.html`) treats every
 * eyebrow / mono label as **weight 400 + uppercase**. Tracking and size then
 * vary by role — measured in
 * `docs/design-system/event-v2-m1-spine-design-diff.md` §3:
 *
 * | Role            | Design | Shipped | Tracking                         |
 * | --------------- | ------ | ------- | -------------------------------- |
 * | Fact chips      | 10px   | 11px    | 0.14em (`--ls-eyebrow`)          |
 * | Panel kicker    | 10px   | 11px    | 0.14em (`--ls-eyebrow`)          |
 * | Category pill   | 11px   | 11px    | 0.14em (`--ls-eyebrow`) → 1.54px |
 * | Panel subline   | 11px   | 11px    | 0.06em                           |
 * | CTA note        | 10px   | 11px    | 0.06em                           |
 * | `<dt>` row key  | 10px   | 11px    | 0.1em                            |
 *
 * WEB-1178 floors every role the design draws at 10px to the 11px `text-micro`
 * step: no text renders below 11px, whatever the mock measured.
 *
 * WEB-704/705/706 shipped a single `11px / 600 / 0.08em` (`--ls-caps`) class
 * because that is what those tickets stated in prose. Both
 * `EventTitleBlock` and `EventDecisionPanel` consume these named treatments
 * so the two call sites cannot drift apart again.
 *
 * Family is composed here, not at the call site: the category pill uses
 * `--font-display` (Space Grotesk); everything else uses `--font-mono`.
 * Colour is still per-call-site (`--fg-2` / `--fg-3` / chroma).
 *
 * `[font-family:…]` is the bracket CSS-property form on purpose —
 * tailwind-merge buckets the `font-[…]` shorthand in the same conflict
 * group as `font-normal`/`font-semibold` and would silently drop the
 * family (same reason `decisionStyles.ts` uses it for the CTA).
 */

/** Shared: weight 400 and eyebrow tracking. Size and family compose below. WEB-1189 C15 drops the uppercase transform so badges stay sentence case. */
export const EVT_MICRO_LABEL_CLASS = 'font-normal tracking-eyebrow';

/**
 * 11px (design: 10px, floored by WEB-1178) / 400 / 0.14em / mono — fact
 * chips and the panel kicker. `--ls-eyebrow` is 0.14em, i.e. 1.54px at 11px.
 */
export const EVT_MICRO_LABEL_10_MONO_CLASS = [
  'text-micro',
  EVT_MICRO_LABEL_CLASS,
  '[font-family:var(--font-mono)]',
].join(' ');

/**
 * Category pill type: 11px / 400 / 0.14em / display.
 * 11px × 0.14em is the measured 1.54px tracking; `leading-[1.45]` plus the
 * pill's 5px/11px padding and 1px border is what lands the 28px height at
 * 1440 (WEB-736 AC). Family is `--font-display`, not mono.
 */
export const EVT_MICRO_LABEL_PILL_CLASS = [
  'text-micro leading-[1.45]',
  EVT_MICRO_LABEL_CLASS,
  '[font-family:var(--font-display)]',
].join(' ');

/** Panel subline — 11px / 400 / 0.06em / mono (measured; not the eyebrow tracking). */
export const EVT_MICRO_LABEL_SUBLINE_CLASS =
  'text-micro font-normal tracking-[0.06em] [font-family:var(--font-mono)]';

/** CTA note — 11px (design: 10px, floored by WEB-1178) / 400 / 0.06em / mono. */
export const EVT_MICRO_LABEL_NOTE_CLASS =
  'text-micro font-normal tracking-[0.06em] [font-family:var(--font-mono)]';

/** `<dt>` row key — 11px (design: 10px, floored by WEB-1178) / 400 / 0.1em / mono. */
export const EVT_MICRO_LABEL_DT_CLASS =
  'text-micro font-normal tracking-[0.1em] [font-family:var(--font-mono)]';
